/**
 * Widening a live offerings list with individually resolved docs.
 *
 * useOfferings() subscribes to the whole `offerings` collection with
 * `orderBy('name')` and no filter (see lib/offerings.ts) - any read-allowed
 * active user can see it, so this was never a permissions gap. But two
 * attendance marks kept rendering their class as a raw doc id
 * (Bobbie's screenshot: "EOmIzdeX", "kVJLl9RV", both 8-char slices of a
 * Firestore auto-id) even though the report and the Jr staff card both
 * already fall back to a raw slice ONLY when `offeringsById.get(id)` comes
 * up empty. Two real ways that happens with the doc still readable:
 *
 *   1. lib/offerings.ts' deleteOffering() hard-deletes via deleteDoc with no
 *      cascade cleanup of the attendance/staffAttendance rows that already
 *      point at it - so a genuinely deleted class's id lives on in old
 *      marks forever.
 *   2. Firestore's orderBy(field) silently EXCLUDES any doc missing that
 *      field from the query results, even though the doc still exists and
 *      is still readable by direct id. A doc written outside the normal
 *      create path (a seed script, a partial write) with no `name` would
 *      never appear in useOfferings() at all, no matter how long the
 *      listener runs.
 *
 * Both cases share one fix: a doc that is not in the LIST subscription can
 * still be checked directly with a getDoc, which needs no ordering and no
 * field to exist. This module is the pure merge/decide half of that -
 * "which ids still need a lookup" and "fold a lookup's result back into the
 * map" - kept free of Firebase and React so it is directly testable. The
 * actual getDoc calls live in lib/offerings.ts' useResolvedOfferingsById,
 * which is this module's only caller.
 */

/**
 * Ids from `neededIds` that are neither already in the live list nor
 * already resolved (found OR confirmed missing) - the ones still worth an
 * individual lookup. Ignores blanks and de-duplicates, so a caller can pass
 * a raw (possibly repeated) list of offeringIds straight from attendance
 * rows with no pre-cleaning.
 */
export function idsStillNeedingResolution(
  neededIds: readonly string[],
  liveById: ReadonlyMap<string, unknown>,
  resolvedExtra: ReadonlyMap<string, unknown>
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of neededIds) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (liveById.has(id) || resolvedExtra.has(id)) continue;
    out.push(id);
  }
  return out;
}

/**
 * The live map, widened with any individually resolved doc that came back
 * FOUND. `resolvedExtra` may also hold `null` entries (a lookup that came
 * back confirmed-missing, tracked so it is never re-fetched) - those are
 * deliberately skipped here rather than merged in as a value, so a
 * genuinely gone offering stays absent from the result and the caller's
 * normal "not found" rendering (an honest label, never a raw id) still
 * applies unchanged. A live entry always wins over a same-id resolved
 * entry, though in practice the two sets are disjoint by construction
 * (idsStillNeedingResolution never asks for an id already in the live map).
 */
export function widenOfferingsById<T>(
  liveById: ReadonlyMap<string, T>,
  resolvedExtra: ReadonlyMap<string, T | null>
): Map<string, T> {
  const out = new Map(liveById);
  for (const [id, value] of resolvedExtra) {
    if (value && !out.has(id)) out.set(id, value);
  }
  return out;
}
